import {
  ArrowPathIcon,
  CheckIcon,
  ChevronDownIcon,
  ClipboardIcon,
  ExclamationCircleIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import { motion, useReducedMotion } from 'framer-motion';
import { PrismWordmark } from '@/shared/ui/prism-wordmark';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

import {
  isChunkLoadError,
  markChunkReloadAttempted,
  shouldAttemptChunkReload,
} from '@/shared/lib/lazy-with-retry';

// djb2 — fast, deterministic, no deps. We only need a stable 8-char identifier
// so support can correlate a customer's screenshot to a Sentry event.
function hashReference(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h + input.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(16).padStart(8, '0').slice(0, 8);
}

function errorMessageOf(err: unknown): string {
  if (err instanceof Error) return err.message || err.name || 'Unknown error';
  if (typeof err === 'string') return err;
  try {
    return String((err as { message?: unknown })?.message ?? '') || 'Unknown error';
  } catch {
    return 'Unknown error';
  }
}

function errorNameOf(err: unknown): string {
  if (err instanceof Error) return err.name;
  if (err && typeof err === 'object' && 'name' in err) return String((err as { name?: unknown }).name ?? 'Error');
  return 'Error';
}

interface CopyPayloadInput {
  reference: string;
  path: string;
  message: string;
  timestamp: string;
}

function formatCopyPayload({ reference, path, message, timestamp }: CopyPayloadInput): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
  const trimmed = message.length > 500 ? `${message.slice(0, 500)}…` : message;
  return [
    `Prism — error reference ${reference}`,
    `Path: ${path}`,
    `Time: ${timestamp}`,
    `Message: ${trimmed}`,
    `User agent: ${ua}`,
  ].join('\n');
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const isChunk = isChunkLoadError(error);
  const reference = useMemo(() => {
    const name = errorNameOf(error);
    const message = errorMessageOf(error);
    const stackTail =
      error instanceof Error && typeof error.stack === 'string' ? error.stack.slice(0, 200) : '';
    return hashReference(`${name}::${message}::${stackTail}`);
  }, [error]);

  const [reloading, setReloading] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'fallback'>('idle');
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  // Safety net: lazyWithRetry already triggers one reload at the import layer.
  // If we reached this boundary because the import-layer reload never fired
  // (non-lazy path, third-party module, etc), give the customer one transparent
  // recovery attempt before showing the UI. shouldAttemptChunkReload coordinates
  // with the lazy layer on a shared sessionStorage timestamp so we never
  // double-reload. Computed during render so the first paint returns null —
  // we don't want the UI to flash before the page tears down.
  const autoReloadInFlight = isChunk && shouldAttemptChunkReload();

  useLayoutEffect(() => {
    if (!autoReloadInFlight) return;
    markChunkReloadAttempted();
    window.location.reload();
  }, [autoReloadInFlight]);

  useEffect(() => {
    document.title = 'Prism';
    // Clear any in-flight toasts (e.g. "Network error" from a concurrently
    // failing fetch). They contradict the boundary's calm framing.
    toast.dismiss();
    const handle = window.setTimeout(() => primaryRef.current?.focus(), 0);
    return () => window.clearTimeout(handle);
  }, []);

  useEffect(() => {
    if (copyState === 'idle') return;
    const handle = window.setTimeout(() => setCopyState('idle'), 1500);
    return () => window.clearTimeout(handle);
  }, [copyState]);

  const handleReload = () => {
    setReloading(true);
    // Clear the guard so the auto-reload path can fire again on this fresh
    // navigation if the asset is still genuinely missing.
    try {
      window.sessionStorage.removeItem('__aurous_chunk_reload_at__');
    } catch {
      // ignore
    }
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleCopy = async () => {
    const payload = formatCopyPayload({
      reference,
      path: typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
      message: errorMessageOf(error),
      timestamp: new Date().toISOString(),
    });
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
        setCopyState('copied');
        return;
      }
    } catch {
      // fall through to manual-select fallback
    }
    setCopyState('fallback');
  };

  // While the safety-net reload is in flight, render nothing so we don't flash
  // the boundary UI before the page tears down.
  if (autoReloadInFlight) return null;

  const heading = isChunk ? 'A new version is available' : 'Something went wrong';
  const subtitle = isChunk
    ? 'We just shipped an update. Reload to get the latest version of Aurous Labs.'
    : 'This page hit an unexpected error. Reloading usually fixes it. If it keeps happening, send us the reference below.';
  const primaryLabel = reloading ? 'Reloading…' : isChunk ? 'Reload now' : 'Reload page';

  const fadeIn = prefersReducedMotion
    ? { initial: false, animate: { opacity: 1, y: 0 }, transition: { duration: 0 } }
    : {
        initial: { opacity: 0, y: 4 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.25, ease: 'easeOut' as const },
      };

  return (
    <main
      role="alert"
      aria-live="polite"
      className="flex min-h-screen w-full items-center justify-center bg-card px-6 py-12"
    >
      <motion.section
        {...fadeIn}
        className="flex w-full max-w-md flex-col items-center text-center gap-6"
      >
        <PrismWordmark />

        <div className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card">
          {isChunk ? (
            <ArrowPathIcon className="h-5 w-5 text-primary" aria-hidden="true" />
          ) : (
            <ExclamationCircleIcon className="h-5 w-5 text-primary" aria-hidden="true" />
          )}
        </div>

        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-display font-semibold tracking-tight text-foreground sm:text-2xl">
            {heading}
          </h1>
          <p className="text-sm leading-relaxed text-muted-foreground">{subtitle}</p>
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <Button
            ref={primaryRef}
            onClick={handleReload}
            isLoading={reloading}
            disabled={reloading}
            className="w-full sm:w-auto focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
          >
            {!reloading && <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />}
            {primaryLabel}
          </Button>
          {!isChunk && (
            <Button
              variant="outline"
              onClick={handleGoHome}
              disabled={reloading}
              className="w-full sm:w-auto focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
            >
              <HomeIcon className="h-4 w-4" aria-hidden="true" />
              Go to dashboard
            </Button>
          )}
        </div>

        {!isChunk && (
          <Collapsible className="w-full border-t border-border pt-4">
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="group w-full justify-between text-muted-foreground hover:text-foreground"
              >
                Technical details
                <ChevronDownIcon className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="relative rounded-md border border-border bg-card p-3 text-left">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCopy}
                  className="absolute top-1.5 right-1.5 h-7 w-7 text-muted-foreground hover:text-foreground"
                  aria-label={copyState === 'copied' ? 'Copied to clipboard' : 'Copy error details'}
                >
                  {copyState === 'copied' ? (
                    <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    <ClipboardIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                </Button>
                <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words pr-8 font-mono text-xs text-muted-foreground">
                  {errorMessageOf(error) || 'No error details available.'}
                </pre>
                {copyState === 'fallback' && (
                  <p className="mt-2 font-mono text-[11px] text-muted-foreground">
                    Press Cmd+C to copy.
                  </p>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        <p className="font-mono text-[11px] tracking-wide text-muted-foreground">
          Error reference: <span data-testid="error-reference">{reference}</span>
        </p>
      </motion.section>
    </main>
  );
}
